import api from '../services/authService';
import axios from 'axios';

/**
 * Generate signed URLs for files
 * @param {Array} files - Array of file metadata objects
 * @returns {Promise} Response containing signed URLs
 */
export const generateSignedUrls = async (presigned_files) => {
 const formData = new FormData();

  // Append values exactly like Postman
  formData.append(
    "presigned_files",
    JSON.stringify(presigned_files.presigned_files)  // must be stringified
  );
  formData.append("matter", presigned_files.matter);
  if (presigned_files.newly_created !== undefined) {
    formData.append('newly_created', presigned_files.newly_created);
  }
   if (presigned_files.folder) {
    formData.append('folder', presigned_files.folder);
  }
  if (presigned_files.relative_path) {
    formData.append('relative_path', presigned_files.relative_path);
  }

  if(presigned_files.override_existing) {
    formData.append('override_existing', true)
  }
  if(presigned_files.override_existing === "folder") {
    formData.append('folder_session_id', presigned_files.batchId)
  }

  const response = await api.post(`/documents/upload/`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response;
};

export const refreshSharePointDocumentChanges = async (documentId) => {
  const response = await api.post(`/documents/${documentId}/sharepoint-refresh/`);
  return response;
};

/**
 * Upload file directly to a signed URL
 * @param {File} file - The file to upload
 * @param {string} signedUrl - The signed URL to upload to
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @param {Function} onUploadProgress - Progress callback
 * @returns {Promise} Response from the upload
 */
export const uploadToSignedUrl = async ({ file, signedUrl, signal, onUploadProgress, contentType }) => {
  const config = {
    headers: {
      'Content-Type': contentType || file.type || 'application/octet-stream',
    },
    signal,
  };

  if (onUploadProgress) {
    config.onUploadProgress = onUploadProgress;
  }

  // Upload directly to the signed URL using PUT.
  //
  // Retry transient failures (network drop, 5xx). Large files ride the
  // longest transfers and are the most exposed to a mid-flight blip —
  // a single 106MB file failing out of an 18-file batch (2026-07-20)
  // is exactly this shape. Deliberate non-retries: user aborts, and
  // 4xx (expired/invalid presign won't get better by retrying).
  //
  // The waits add up to ~17s so a Wi-Fi hiccup of the usual length is
  // absorbed silently; 20 files in a row dying at the tail of a 400-file
  // claim-file drop (2026-10-06) outlasted the old ~3s budget. Anything
  // longer than this lands in the Failed tab with a one-click Retry.
  const retryDelaysMs = [2000, 5000, 10000];
  const maxAttempts = retryDelaysMs.length + 1;
  let lastError;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await axios.put(signedUrl, file, config);
    } catch (error) {
      lastError = error;
      const aborted = signal?.aborted || error?.name === 'CanceledError';
      const status = error?.response?.status;
      const retriable = !aborted && (!status || status >= 500);
      if (!retriable || attempt === maxAttempts) {
        throw error;
      }
      console.warn(
        `S3 upload attempt ${attempt} failed for ${file?.name}; retrying`,
        error?.message
      );
      await new Promise((resolve) => setTimeout(resolve, retryDelaysMs[attempt - 1]));
    }
  }
  throw lastError;
};

/**
 * Upload file to SharePoint URL in chunks
 * @param {File} file - The file to upload
 * @param {string} sharepointUrl - The SharePoint URL to upload to
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @param {Function} onUploadProgress - Progress callback
 * @param {number} chunkSize - Size of each chunk in bytes (default: 4MB)
 * @returns {Promise} Response from the upload
 */
export const uploadToSharePointInChunks = async ({ 
  file, 
  sharepointUrl, 
  signal, 
  onUploadProgress,
  chunkSize = 4 * 1024 * 1024 // 4MB default chunk size
}) => {
  const fileSize = file.size;
  const totalChunks = Math.ceil(fileSize / chunkSize);
  let uploadedBytes = 0;

  // Upload each chunk
  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
    // Check if upload was cancelled
    if (signal?.aborted) {
      throw new Error('Upload cancelled');
    }

    const start = chunkIndex * chunkSize;
    const end = Math.min(start + chunkSize, fileSize);
    const chunk = file.slice(start, end);
    
    // Calculate range for this chunk
    const rangeStart = start;
    const rangeEnd = end - 1;
    const contentRange = `bytes ${rangeStart}-${rangeEnd}/${fileSize}`;

    const config = {
      headers: {
        'Content-Type': 'application/octet-stream',
        'Content-Range': contentRange,
        'Content-Length': chunk.size.toString(),
      },
      signal,
    };

    try {
      // Upload chunk using PUT with Content-Range header
      await axios.put(sharepointUrl, chunk, config);
      
      uploadedBytes += chunk.size;
      
      // Update progress
      if (onUploadProgress) {
        // Call progress callback with loaded and total bytes
        onUploadProgress({
          loaded: uploadedBytes,
          total: fileSize,
        });
      }
    } catch (error) {
      // If it's a cancellation, throw immediately
      if (signal?.aborted || error.name === 'AbortError') {
        throw new Error('Upload cancelled');
      }
      
      // For other errors, rethrow
      throw error;
    }
  }

  // Return success response
  return { data: { success: true, uploadedBytes, fileSize } };
};

export const uploadMatterDetail = async ({ file, matter, folder, newly_created, relative_path, signal, onUploadProgress, override_existing, batchId }) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('matter', matter);
  if (folder) {
    formData.append('folder', folder);
  }
  if (newly_created !== undefined) {
    formData.append('newly_created', newly_created);
  }
  if (relative_path) {
    formData.append('relative_path', relative_path);
  }

  if(override_existing) {
    formData.append('override_existing', true)
  }
  if(override_existing === "folder" && relative_path) {
    formData.append('folder_session_id', batchId)
  }

  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  
  if (signal) {
    config.signal = signal;
  }
  
  if (onUploadProgress) {
    config.onUploadProgress = onUploadProgress;
  }
  
  const response = await api.post(`/documents/upload/`, formData, config);
  return response
}

export const createFolder = async ({ name, matter_id, parent_id }) => {
  const response = await api.post(`/folders/create/`, { name, matter_id, parent_id });
  return response
}

export const savePDF = async (docID, file) => {
  const response = await api.post(`/documents/save/`, { file: file, document_id: docID, auto_save: true }, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response;
}

export const beginPDFFinalization = async (docID) => {
  return api.post(`/documents/${docID}/finalize-session/`);
}

export const cancelPDFFinalization = async (docID, finalizationToken) => {
  return api.delete(`/documents/${docID}/finalize-session/`, {
    data: { finalization_token: finalizationToken },
  });
}

export const finalizePDF = async (docID, finalizationToken) => {
  const response = await api.post(`/documents/${docID}/finalize/`, {
    finalization_token: finalizationToken,
  });
  return response;
}

export const renameFolder = async ({ id, name }) => {
  const response = await api.post(`/folders/${id}/rename/`, { name });
  return response
}

/**
 * Complete upload process by notifying backend of successful uploads
 * @param {Array} documentIds - Array of document IDs that were successfully uploaded
 * @returns {Promise} Response from the complete API
 */
export const completeUpload = async (documentIds, { aiProcess = false } = {}) => {
  // ai_process is only sent for the AI Claim File Upload route. Product
  // ruling (2026-07-22): "Mail gets the full treatment. AI Claim File
  // Upload gets the full treatment. Everything else, no treatment."
  // The backend honors this marker for admin/super_admin only.
  const payload = { document_ids: documentIds };
  if (aiProcess) {
    payload.ai_process = true;
  }
  const response = await api.post(`/documents/upload/complete/`, payload);
  return response;
}